import React, { useState, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Dialog,
  DialogContent, DialogActions, IconButton, Chip, CircularProgress, Alert, Tabs, Tab,
  TextField, InputAdornment, Grid, Select, MenuItem, FormControl, InputLabel,
  Card, CardContent, CardActions, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import { iconButtonStyles, StyledDialogTitle, StyledTableContainer, tableRowHoverStyles } from '../components/StyledComponents';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import SubjectIcon from '@mui/icons-material/Subject';
import PhoneIcon from '@mui/icons-material/Phone';
import { useQuery, useMutation, gql } from '@apollo/client';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import AuthContext from '../contexts/AuthContext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

// GraphQL queries et mutations
const GET_APPOINTMENTS = gql`
  query GetAppointments($status: String, $departmentId: ID, $assignedToId: ID) {
    appointments(status: $status, departmentId: $departmentId, assignedToId: $assignedToId) {
      id
      email
      firstName
      lastName
      phoneNumber
      department {
        id
        name
      }
      subject
      description
      scheduledAt
      status
      assignedTo {
        id
        firstName
        lastName
      }
      notes
      cancellationReason
      visit {
        id
        status
      }
      createdAt
      updatedAt
      confirmedAt
      completedAt
      visitRegistrationToken
      visitRegistrationLink
      visitRegistrationQrBase64
      documents {
        id
        fileName
        documentUrl
      }
    }
  }
`;

const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
    }
  }
`;

const CONFIRM_APPOINTMENT = gql`
  mutation ConfirmAppointment($id: ID!, $assignedToId: ID, $scheduledAt: DateTime, $notes: String) {
    confirmAppointment(id: $id, assignedToId: $assignedToId, scheduledAt: $scheduledAt, notes: $notes) {
      success
      message
      appointment {
        id
        status
        visit {
          id
          status
        }
      }
      visit {
        id
        status
      }
      userCreated
      password
      visitRegistrationLink
      visitRegistrationQrBase64
    }
  }
`;

const CREATE_VISIT_FROM_APPOINTMENT = gql`
  mutation CreateVisitFromAppointment($appointmentId: ID!, $registrationToken: String!, $notes: String) {
    createVisitFromAppointment(
      appointmentId: $appointmentId
      registrationToken: $registrationToken
      notes: $notes
    ) {
      success
      message
      visit {
        id
        status
      }
      appointment {
        id
        status
        visit {
          id
          status
        }
      }
    }
  }
`;

const CANCEL_APPOINTMENT = gql`
  mutation CancelAppointment($id: ID!, $cancellationReason: String!) {
    cancelAppointment(id: $id, cancellationReason: $cancellationReason) {
      success
      message
      appointment {
        id
        status
        cancellationReason
      }
    }
  }
`;

const GET_HOSTS = gql`
  query GetHosts($departmentId: ID!) {
    users(role: "HOST", departmentId: $departmentId) {
      id
      firstName
      lastName
      email
      department {
        id
        name
      }
    }
  }
`;

function Appointments() {
  const APPOINTMENT_START_HOUR = 7;
  const APPOINTMENT_END_HOUR = 23;
  const { user } = useContext(AuthContext);
  const allowedRoles = ['ADMIN', 'MANAGER', 'ACCEUIL', 'HOST', 'VISITOR'];
  const canFilterByDepartment = user?.role === 'ADMIN';
  const canViewDocuments = user?.role !== 'ACCEUIL';
  const isVisitorView = user?.role === 'VISITOR';
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  
  // État pour les filtres
  const [statusFilter, setStatusFilter] = useState('');
  // Les MANAGER ne peuvent voir que leur département
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Garder le filtre département aligné avec les permissions réelles.
  useEffect(() => {
    if ((user?.role === 'MANAGER' || user?.role === 'ACCEUIL') && user?.department?.id) {
      setDepartmentFilter(user.department.id);
      return;
    }
    if (!canFilterByDepartment) {
      setDepartmentFilter('');
    }
  }, [user, canFilterByDepartment]);

  // Effacer le paramètre highlight après l'avoir utilisé
  useEffect(() => {
    if (highlightId) {
      // Faire défiler vers l'élément mis en évidence après un court délai
      setTimeout(() => {
        const element = document.getElementById(`appointment-${highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Supprimer le paramètre de l'URL après le défilement
          setTimeout(() => {
            searchParams.delete('highlight');
            setSearchParams(searchParams, { replace: true });
          }, 1000);
        }
      }, 500);
    }
  }, [highlightId, searchParams, setSearchParams]);
  
  // États pour les dialogues
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  
  // État pour le rendez-vous sélectionné
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedHostId, setSelectedHostId] = useState('');
  const [confirmScheduledAt, setConfirmScheduledAt] = useState(null);
  const [notes, setNotes] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  
  // État pour les messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Requête pour récupérer les rendez-vous
  // Les MANAGER voient uniquement les rendez-vous de leur département
  const getAppointmentsVariables = () => {
    const vars = {
      status: null,
      departmentId: canFilterByDepartment ? (departmentFilter || null) : null,
      assignedToId: user?.role === 'HOST' ? user.id : null,
    };
    // Si c'est un MANAGER ou ACCEUIL, toujours filtrer par son département
    if ((user?.role === 'MANAGER' || user?.role === 'ACCEUIL') && user?.department?.id) {
      vars.departmentId = user.department.id;
    }
    return vars;
  };

  const { data, loading, error, refetch } = useQuery(GET_APPOINTMENTS, {
    variables: getAppointmentsVariables(),
    skip: !user || !allowedRoles.includes(user.role),
  });

  // Requête pour récupérer les départements
  const { data: departmentsData } = useQuery(GET_DEPARTMENTS, {
    skip: !canFilterByDepartment,
  });

  // Requête pour récupérer les hôtes d'un département
  const { data: hostsData } = useQuery(GET_HOSTS, {
    variables: { departmentId: selectedAppointment?.department?.id },
    skip: !selectedAppointment || !openConfirmDialog,
  });

  // Mutations
  const [confirmAppointment] = useMutation(CONFIRM_APPOINTMENT, {
    onCompleted: (data) => {
      if (data.confirmAppointment.success) {
        let message = data.confirmAppointment.message;
        // Ajouter des informations supplémentaires si un utilisateur a été créé
        if (data.confirmAppointment.userCreated) {
          message += ` Les identifiants ont été envoyés par email à ${selectedAppointment?.email}.`;
        }
        setSuccessMessage(message);
        setErrorMessage('');
        setOpenConfirmDialog(false);
        setSelectedAppointment(null);
        setSelectedHostId('');
        setConfirmScheduledAt(null);
        setNotes('');
        refetch();
        // Afficher le message de succès pendant 8 secondes (plus long pour lire les infos)
        setTimeout(() => setSuccessMessage(''), 8000);
      } else {
        setErrorMessage(data.confirmAppointment.message);
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      console.error('Erreur lors de la confirmation du rendez-vous:', error);
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message || 'Une erreur est survenue lors de la confirmation du rendez-vous';
      setErrorMessage(errorMessage);
      setSuccessMessage('');
    },
  });

  const [cancelAppointment] = useMutation(CANCEL_APPOINTMENT, {
    onCompleted: (data) => {
      if (data.cancelAppointment.success) {
        setSuccessMessage(data.cancelAppointment.message);
        setErrorMessage('');
        setOpenCancelDialog(false);
        setSelectedAppointment(null);
        setCancellationReason('');
        refetch();
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setErrorMessage(data.cancelAppointment.message);
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      setErrorMessage(error.message);
      setSuccessMessage('');
    },
  });

  const [createVisitFromAppointment] = useMutation(CREATE_VISIT_FROM_APPOINTMENT, {
    onCompleted: (data) => {
      if (data.createVisitFromAppointment.success) {
        setSuccessMessage(data.createVisitFromAppointment.message);
        setErrorMessage('');
        refetch();
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setErrorMessage(data.createVisitFromAppointment.message);
        setSuccessMessage('');
      }
    },
    onError: (error) => {
      const msg = error.graphQLErrors?.[0]?.message || error.message;
      setErrorMessage(msg);
      setSuccessMessage('');
    },
  });

  // Handlers
  const handleOpenDetails = (appointment) => {
    setSelectedAppointment(appointment);
    setOpenDetailsDialog(true);
  };

  const handleOpenConfirm = (appointment) => {
    setSelectedAppointment(appointment);
    setSelectedHostId('');
    setConfirmScheduledAt(appointment?.scheduledAt ? new Date(appointment.scheduledAt) : null);
    setNotes('');
    setOpenConfirmDialog(true);
  };

  const handleOpenCancel = (appointment) => {
    setSelectedAppointment(appointment);
    setCancellationReason('');
    setOpenCancelDialog(true);
  };

  const handleConfirm = () => {
    if (!selectedAppointment) return;
    if (!confirmScheduledAt || confirmScheduledAt <= new Date()) {
      setErrorMessage("La date et l'heure doivent être dans le futur");
      return;
    }
    const hour = confirmScheduledAt.getHours();
    if (hour < APPOINTMENT_START_HOUR || hour >= APPOINTMENT_END_HOUR) {
      setErrorMessage("L'heure du rendez-vous doit être comprise entre 07:00 et 23:00");
      return;
    }
    confirmAppointment({
      variables: {
        id: selectedAppointment.id,
        assignedToId: selectedHostId || null,
        scheduledAt: confirmScheduledAt.toISOString(),
        notes: notes || null,
      },
    });
  };

  const handleCreateVisitFromAppointment = (appointment) => {
    if (!appointment?.visitRegistrationToken) {
      setErrorMessage("Token d'enregistrement introuvable pour ce rendez-vous");
      return;
    }
    createVisitFromAppointment({
      variables: {
        appointmentId: appointment.id,
        registrationToken: appointment.visitRegistrationToken,
        notes: appointment.notes || null,
      },
    });
  };

  const handleCancel = () => {
    if (!selectedAppointment || !cancellationReason.trim()) {
      setErrorMessage('Le motif d\'annulation est obligatoire');
      return;
    }
    cancelAppointment({
      variables: {
        id: selectedAppointment.id,
        cancellationReason: cancellationReason,
      },
    });
  };

  // Fonctions utilitaires
  const getStatusLabel = (status) => {
    const labels = {
      'PENDING': 'En attente',
      'CONFIRMED': 'Confirmé',
      'CANCELLED': 'Annulé',
      'COMPLETED': 'Terminé',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDING': 'warning',
      'CONFIRMED': 'info',
      'CANCELLED': 'error',
      'COMPLETED': 'success',
    };
    return colors[status] || 'default';
  };

  const isToday = (isoDate) => {
    const target = new Date(isoDate);
    const now = new Date();
    return (
      target.getFullYear() === now.getFullYear() &&
      target.getMonth() === now.getMonth() &&
      target.getDate() === now.getDate()
    );
  };

  const renderDocumentActions = (documents = [], compact = true) => {
    if (!documents.length) {
      return <Typography variant="body2" color="text.secondary">-</Typography>;
    }

    if (compact) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {documents.map((doc) => (
            <IconButton
              key={doc.id}
              size="small"
              component="a"
              href={doc.documentUrl}
              target="_blank"
              rel="noreferrer"
              title={`Télécharger ${doc.fileName}`}
              sx={iconButtonStyles}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          ))}
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {documents.map((doc) => (
          <Button
            key={doc.id}
            component="a"
            href={doc.documentUrl}
            target="_blank"
            rel="noreferrer"
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ width: 'fit-content' }}
          >
            {doc.fileName}
          </Button>
        ))}
      </Box>
    );
  };

  const renderAppointmentActions = (appointment) => (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<VisibilityIcon />}
        onClick={() => handleOpenDetails(appointment)}
        sx={{
          borderColor: '#006d78',
          color: '#006d78',
          borderRadius: 2,
          fontSize: '0.75rem',
          '&:hover': { backgroundColor: '#f0fafb' },
        }}
      >
        Détails
      </Button>

      {appointment.status === 'PENDING' && (
        <>
          {(user?.role === 'ADMIN' || (user?.role === 'MANAGER' && appointment.department?.id === user?.department?.id) || (user?.role === 'HOST' && appointment.assignedTo?.id === user?.id)) && (
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleOpenConfirm(appointment)}
              sx={{
                background: 'linear-gradient(135deg, #006d78, #005a63)',
                borderRadius: 2,
                fontSize: '0.75rem',
                '&:hover': { background: 'linear-gradient(135deg, #005a63, #003d45)' },
              }}
            >
              Confirmer
            </Button>
          )}
          {(user?.role === 'ADMIN' || user?.role === 'MANAGER' || (user?.role === 'HOST' && appointment.assignedTo?.id === user?.id)) && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => handleOpenCancel(appointment)}
              sx={{
                borderColor: '#d21433',
                color: '#d21433',
                borderRadius: 2,
                fontSize: '0.75rem',
                '&:hover': { backgroundColor: '#fff5f5' },
              }}
            >
              Annuler
            </Button>
          )}
        </>
      )}

      {appointment.status === 'CONFIRMED' && !appointment.visit && ['ADMIN', 'MANAGER', 'ACCEUIL'].includes(user?.role) && isToday(appointment.scheduledAt) && (
        <Button
          size="small"
          variant="contained"
          onClick={() => handleCreateVisitFromAppointment(appointment)}
          sx={{ background: 'linear-gradient(135deg, #4caf50, #388e3c)', borderRadius: 2, fontSize: '0.75rem' }}
        >
          Créer visite
        </Button>
      )}
    </>
  );

  // Filtrer les rendez-vous selon la recherche
  const allAppointments = data?.appointments || [];

  const filteredAppointments = allAppointments.filter((appointment) => {
    if (statusFilter && appointment.status !== statusFilter) {
      return false;
    }

    if (!searchQuery) {
      return true;
    }

    const query = searchQuery.toLowerCase();
    return (
      appointment.email?.toLowerCase().includes(query) ||
      appointment.firstName?.toLowerCase().includes(query) ||
      appointment.lastName?.toLowerCase().includes(query) ||
      appointment.subject?.toLowerCase().includes(query) ||
      appointment.department?.name?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error">Erreur lors du chargement des rendez-vous: {error.message}</Alert>
      </Container>
    );
  }

  if (!allowedRoles.includes(user?.role)) {
    return (
      <Container>
        <Alert severity="error">Vous n'avez pas accès à cette page.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonthIcon sx={{ color: '#006d78' }} /> Gestion des Rendez-vous
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
            Gérez les demandes de rendez-vous et transformez-les en visites après validation
          </Typography>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* Search + Department filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Rechercher par email, nom, sujet..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#006d78', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        {canFilterByDepartment && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Département</InputLabel>
            <Select
              value={departmentFilter}
              label="Département"
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <MenuItem value="">Tous</MenuItem>
              {departmentsData?.departments?.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Tabs by status */}
      <Box sx={{ borderBottom: '2px solid #e0e0e0', mb: 3 }}>
        <Tabs
          value={statusFilter}
          onChange={(_, v) => setStatusFilter(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ style: { backgroundColor: '#006d78', height: 3 } }}
          sx={{ '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '0.9rem' } }}
        >
          {[
            { value: '', label: 'Tous', color: '#607d8b' },
            { value: 'PENDING', label: 'En attente', color: '#f59e0b' },
            { value: 'CONFIRMED', label: 'Confirmés', color: '#006d78' },
            { value: 'COMPLETED', label: 'Terminés', color: '#4caf50' },
            { value: 'CANCELLED', label: 'Annulés', color: '#d21433' },
          ].map(tab => {
            const count = tab.value === ''
              ? allAppointments.length
              : allAppointments.filter((appointment) => appointment.status === tab.value).length;
            return (
              <Tab
                key={tab.value}
                value={tab.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    {tab.label}
                    <Box sx={{
                      background: statusFilter === tab.value ? tab.color : '#e0e0e0',
                      color: statusFilter === tab.value ? '#fff' : '#666',
                      borderRadius: 10, px: 1, py: 0.1, fontSize: '0.72rem', fontWeight: 700, minWidth: 22, textAlign: 'center',
                    }}>
                      {count}
                    </Box>
                  </Box>
                }
                sx={{ color: statusFilter === tab.value ? tab.color : '#888' }}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Visitor: cards, others: table */}
      {isVisitorView ? (
        filteredAppointments.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, color: '#bbb' }}>
            <CalendarMonthIcon sx={{ fontSize: 56, mb: 2, opacity: 0.4 }} />
            <Typography variant="h6" sx={{ color: '#aaa' }}>Aucun rendez-vous trouvé</Typography>
          </Box>
        ) : (
        <Grid container spacing={2}>
          {filteredAppointments.map((appointment) => {
            const statusColors = { PENDING: '#f59e0b', CONFIRMED: '#006d78', COMPLETED: '#4caf50', CANCELLED: '#d21433' };
            const sc = statusColors[appointment.status] || '#888';
            const isHighlighted = highlightId === appointment.id;

            return (
              <Grid item xs={12} sm={6} md={4} key={appointment.id} id={`appointment-${appointment.id}`}>
                <Card elevation={isHighlighted ? 6 : 1} sx={{
                  borderRadius: 3,
                  border: `1px solid ${isHighlighted ? '#006d78' : '#e8e8e8'}`,
                  borderLeft: `4px solid ${sc}`,
                  transition: 'all 0.2s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': { boxShadow: '0 6px 24px rgba(0,116,129,0.12)', transform: 'translateY(-2px)' },
                }}>
                  <CardContent sx={{ flex: 1, pb: 1 }}>
                    {/* Header card */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1a1a1a', lineHeight: 1.2 }}>
                          {appointment.firstName} {appointment.lastName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#888' }}>{appointment.email}</Typography>
                      </Box>
                      <Chip
                        label={getStatusLabel(appointment.status)}
                        size="small"
                        sx={{
                          backgroundColor: sc + '18',
                          color: sc,
                          fontWeight: 700,
                          border: `1px solid ${sc}44`,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>

                    {/* Details */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <BusinessIcon sx={{ fontSize: 14, color: '#006d78' }} />
                        <Typography variant="caption" sx={{ color: '#555' }}>{appointment.department?.name || '—'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                        <SubjectIcon sx={{ fontSize: 14, color: '#006d78', mt: '2px' }} />
                        <Typography variant="caption" sx={{ color: '#555', lineHeight: 1.4 }}>{appointment.subject}</Typography>
                      </Box>
                      {appointment.phoneNumber && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: '#006d78' }} />
                          <Typography variant="caption" sx={{ color: '#555' }}>{appointment.phoneNumber}</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <CalendarMonthIcon sx={{ fontSize: 14, color: '#006d78' }} />
                        <Typography variant="caption" fontWeight={600} sx={{ color: '#333' }}>
                          {format(new Date(appointment.scheduledAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                        </Typography>
                      </Box>
                      {appointment.assignedTo && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <PersonIcon sx={{ fontSize: 14, color: '#006d78' }} />
                          <Typography variant="caption" sx={{ color: '#555' }}>
                            {appointment.assignedTo.firstName} {appointment.assignedTo.lastName}
                          </Typography>
                        </Box>
                      )}
                      {appointment.visit && (
                        <Chip label="Visite créée" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#4caf50', fontWeight: 700, width: 'fit-content', mt: 0.5, fontSize: '0.7rem' }} />
                      )}
                      {canViewDocuments && appointment.documents?.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {appointment.documents.map((doc) => (
                            <IconButton key={doc.id} size="small" component="a" href={doc.documentUrl} target="_blank" rel="noreferrer" title={`Télécharger ${doc.fileName}`} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 0.3 }}>
                              <DownloadIcon fontSize="small" sx={{ fontSize: 14 }} />
                            </IconButton>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2, pt: 0, gap: 1, flexWrap: 'wrap' }}>
                    {renderAppointmentActions(appointment)}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        )
      ) : (
        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Demandeur</TableCell>
                <TableCell>Département</TableCell>
                <TableCell>Sujet</TableCell>
                <TableCell>Date et heure</TableCell>
                <TableCell>Assigné à</TableCell>
                <TableCell>Statut</TableCell>
                {canViewDocuments && <TableCell>Pièces jointes</TableCell>}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAppointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canViewDocuments ? 8 : 7} align="center">
                    {searchQuery ? 'Aucun rendez-vous ne correspond à votre recherche' : 'Aucun rendez-vous trouvé'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAppointments.map((appointment, index) => (
                  <TableRow
                    key={appointment.id}
                    id={`appointment-${appointment.id}`}
                    sx={{
                      backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                      ...(highlightId === appointment.id ? { backgroundColor: 'rgba(0, 109, 120, 0.08)' } : {}),
                      ...tableRowHoverStyles,
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {appointment.firstName} {appointment.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {appointment.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{appointment.department?.name || '—'}</TableCell>
                    <TableCell>{appointment.subject}</TableCell>
                    <TableCell>
                      {format(new Date(appointment.scheduledAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                    </TableCell>
                    <TableCell>
                      {appointment.assignedTo
                        ? `${appointment.assignedTo.firstName} ${appointment.assignedTo.lastName}`
                        : 'Non assigné'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(appointment.status)}
                        color={getStatusColor(appointment.status)}
                        size="small"
                      />
                    </TableCell>
                    {canViewDocuments && <TableCell>{renderDocumentActions(appointment.documents)}</TableCell>}
                    <TableCell sx={{ whiteSpace: 'nowrap', '& > *': { mr: 0.5, mb: 0.5 } }}>
                      {renderAppointmentActions(appointment)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}

      {/* Dialogue des détails */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <StyledDialogTitle>Détails du rendez-vous</StyledDialogTitle>
        <DialogContent>
          {selectedAppointment && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedAppointment.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Nom complet</Typography>
                <Typography variant="body1">{selectedAppointment.firstName} {selectedAppointment.lastName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Téléphone</Typography>
                <Typography variant="body1">{selectedAppointment.phoneNumber || 'Non renseigné'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Département</Typography>
                <Typography variant="body1">{selectedAppointment.department?.name}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Sujet</Typography>
                <Typography variant="body1">{selectedAppointment.subject}</Typography>
              </Grid>
              {selectedAppointment.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{selectedAppointment.description}</Typography>
                </Grid>
              )}
              {canViewDocuments && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Pièces jointes</Typography>
                  <Box sx={{ mt: 1 }}>
                    {renderDocumentActions(selectedAppointment.documents, false)}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Date prévue</Typography>
                <Typography variant="body1">
                  {format(new Date(selectedAppointment.scheduledAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Statut</Typography>
                <Chip
                  label={getStatusLabel(selectedAppointment.status)}
                  color={getStatusColor(selectedAppointment.status)}
                  size="small"
                />
              </Grid>
              {selectedAppointment.assignedTo && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Assigné à</Typography>
                  <Typography variant="body1">
                    {selectedAppointment.assignedTo.firstName} {selectedAppointment.assignedTo.lastName}
                  </Typography>
                </Grid>
              )}
              {selectedAppointment.visitRegistrationLink && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Lien d'enregistrement visite</Typography>
                  <Typography
                    component="a"
                    href={selectedAppointment.visitRegistrationLink}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ wordBreak: 'break-all' }}
                  >
                    Cliquer ici pour ouvrir le lien d'enregistrement
                  </Typography>
                </Grid>
              )}
              {selectedAppointment.visitRegistrationQrBase64 && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">QR d'enregistrement</Typography>
                  <Box
                    component="img"
                    alt="QR Code enregistrement visite"
                    src={`data:image/png;base64,${selectedAppointment.visitRegistrationQrBase64}`}
                    sx={{ width: 180, height: 180, mt: 1, border: '1px solid #ddd', borderRadius: 1 }}
                  />
                </Grid>
              )}
              {selectedAppointment.visit && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Visite créée</Typography>
                  <Chip label={`Visite #${selectedAppointment.visit.id}`} color="success" size="small" />
                </Grid>
              )}
              {selectedAppointment.cancellationReason && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Motif d'annulation</Typography>
                  <Typography variant="body1">{selectedAppointment.cancellationReason}</Typography>
                </Grid>
              )}
              {selectedAppointment.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Notes internes</Typography>
                  <Typography variant="body1">{selectedAppointment.notes}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Créé le</Typography>
                <Typography variant="body2">
                  {format(new Date(selectedAppointment.createdAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de confirmation */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)} maxWidth="sm" fullWidth>
        <StyledDialogTitle>Confirmer le rendez-vous</StyledDialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            La validation confirme le rendez-vous et génère un lien/QR pour l'enregistrement de la visite à l'accueil.
          </Alert>
          {selectedAppointment && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Demandeur:</strong> {selectedAppointment.firstName} {selectedAppointment.lastName} ({selectedAppointment.email})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Sujet:</strong> {selectedAppointment.subject}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Département:</strong> {selectedAppointment.department?.name}
                </Typography>
              </Grid>
              {user?.role !== 'HOST' && hostsData?.users && hostsData.users.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Assigner à un hôte (optionnel)</InputLabel>
                    <Select
                      value={selectedHostId}
                      label="Assigner à un hôte (optionnel)"
                      onChange={(e) => setSelectedHostId(e.target.value)}
                    >
                      <MenuItem value="">Sélectionner automatiquement</MenuItem>
                      {hostsData.users.map((host) => (
                        <MenuItem key={host.id} value={host.id}>
                          {host.firstName} {host.lastName} ({host.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
                  <DateTimePicker
                    label="Date et heure de validation"
                    value={confirmScheduledAt}
                    onChange={setConfirmScheduledAt}
                    minDateTime={new Date()}
                    shouldDisableTime={(value, view) => {
                      if (view !== 'hours') return false;
                      const hour = typeof value === 'number'
                        ? value
                        : (value?.getHours?.() ?? value?.hour?.() ?? 0);
                      return hour < APPOINTMENT_START_HOUR || hour >= APPOINTMENT_END_HOUR;
                    }}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Notes internes (optionnel)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Annuler</Button>
          <Button onClick={handleConfirm} variant="contained" color="success">
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue d'annulation */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)} maxWidth="sm" fullWidth>
        <StyledDialogTitle>{user?.role === 'HOST' ? 'Rejeter le rendez-vous' : 'Annuler le rendez-vous'}</StyledDialogTitle>
        <DialogContent>
          {selectedAppointment && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Demandeur:</strong> {selectedAppointment.firstName} {selectedAppointment.lastName} ({selectedAppointment.email})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Sujet:</strong> {selectedAppointment.subject}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={4}
                  label={user?.role === 'HOST' ? "Motif du rejet *" : "Motif d'annulation *"}
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  error={!cancellationReason.trim()}
                  helperText={user?.role === 'HOST' ? "Le motif du rejet est obligatoire" : "Le motif d'annulation est obligatoire"}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>Annuler</Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            color="error"
            disabled={!cancellationReason.trim()}
          >
            {user?.role === 'HOST' ? 'Rejeter le rendez-vous' : 'Annuler le rendez-vous'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Appointments;
